// frontend/src/pages/ServiceBookingPage.js
import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { vehiclesApi } from '../api/vehicles';
import { servicesApi } from '../api/services';
import { 
  FaCar, FaTools, FaCalendarAlt, FaHome, FaBuilding, 
  FaCheckCircle, FaExclamationTriangle, FaTruck, FaMapMarkerAlt 
} from 'react-icons/fa';

const SERVICE_CATEGORIES = [
  { id: 'routine_maintenance', label: 'Routine Maintenance (Oil, Fluids, Filters)' },
  { id: 'repair_services', label: 'General Repair (Brakes, Suspension, Engine)' },
  { id: 'diagnostic_services', label: 'Diagnostics (Check Engine Light, Strange Noises)' },
  { id: 'inspection_services', label: 'Pre-Purchase / Safety Inspection' },
  { id: 'tire_services', label: 'Tire Services (Rotation, Alignment)' },
  { id: 'body_work', label: 'Body Work & Painting' },
  { id: 'hvac_services', label: 'AC & Heating' },
  { id: 'electrical_repairs', label: 'Electrical System' },
];

function ServiceBookingPage() {
  const location = useLocation();
  
  // Check if a vehicle ID was passed via URL
  const queryParams = new URLSearchParams(location.search);
  const preSelectedVehicleId = queryParams.get('vehicle');

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    vehicle_id: preSelectedVehicleId || '',
    service_category_id: 'routine_maintenance',
    service_type: 'workshop', // 'workshop', 'home_service', 'roadside_assistance'
    preferred_date: '',
    preferred_time_slot: 'Morning (8am - 12pm)',
    description: '',
    address: '', // Required for home_service and roadside_assistance
    requires_towing: false // Only for roadside_assistance
  });

  useEffect(() => {
    const fetchGarage = async () => {
      try {
        const data = await vehiclesApi.getGarage();
        setVehicles(data);
        
        // If only one car exists, auto-select it
        if (data.length === 1) {
            setFormData(prev => {
                if (!prev.vehicle_id) {
                    return { ...prev, vehicle_id: data[0].id };
                }
                return prev;
            });
        }
      } catch (err) {
        console.error("Error fetching garage:", err);
        setError("Failed to load your vehicles.");
      } finally {
        setLoading(false);
      }
    };
    fetchGarage();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleServiceTypeChange = (type) => {
      setFormData(prev => ({
          ...prev,
          service_type: type,
          // Reset towing if switching away from roadside
          requires_towing: type === 'roadside_assistance' ? prev.requires_towing : false
      }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!formData.vehicle_id) {
      setError("Please select a vehicle.");
      setSubmitting(false);
      return;
    }

    // Validate Address for remote services
    if ((formData.service_type === 'home_service' || formData.service_type === 'roadside_assistance') && !formData.address) {
      setError(formData.service_type === 'home_service' ? "Please provide your home address." : "Please provide your breakdown location.");
      setSubmitting(false);
      return;
    }

    try {
      await servicesApi.bookService(formData);
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error("Booking error:", err);
      setError(err.message || "Failed to submit booking.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-center py-20">Loading booking form...</div>;

  if (success) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-gray-50 px-4 text-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <FaCheckCircle className="text-green-500 text-6xl mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Booking Received!</h1>
          <p className="text-gray-600 mb-8">
            Your request has been submitted. Our team will review it and contact you shortly to confirm the appointment.
          </p>
          <div className="space-y-3">
            <Link 
              to="/account" 
              className="block w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-lg transition duration-300"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 flex items-center gap-2">
          <FaCalendarAlt className="text-primary" /> Schedule Service
        </h1>

        {vehicles.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow-sm text-center">
            <FaExclamationTriangle className="text-yellow-500 text-4xl mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">No Vehicles Found</h2>
            <p className="text-gray-600 mb-6">You need to add a vehicle to your garage before booking a service.</p>
            <Link to="/account" className="bg-primary text-white px-6 py-2 rounded hover:bg-primary-dark">
              Go to My Garage
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 md:p-8">
            
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                {error}
              </div>
            )}

            {/* 1. Vehicle Selection */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">Select Vehicle</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {vehicles.map((v) => (
                  <div 
                    key={v.id}
                    onClick={() => setFormData({ ...formData, vehicle_id: v.id })}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all flex items-center gap-3 ${
                      formData.vehicle_id === v.id 
                        ? 'border-primary bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <FaCar className={formData.vehicle_id === v.id ? 'text-primary' : 'text-gray-400'} size={24} />
                    <div>
                      <div className="font-bold text-gray-800">{v.year} {v.make} {v.model}</div>
                      <div className="text-xs text-gray-500">{v.plate_number || 'No Plate Info'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 2. Service Type */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">Service Location</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                
                {/* Workshop */}
                <div 
                    onClick={() => handleServiceTypeChange('workshop')}
                    className={`border-2 rounded-lg p-4 cursor-pointer flex flex-col items-center justify-center gap-2 transition-all ${formData.service_type === 'workshop' ? 'border-primary bg-blue-50 text-primary' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  <FaBuilding size={24} />
                  <span className="font-bold text-sm">Workshop Visit</span>
                </div>

                {/* Home Service */}
                <div 
                    onClick={() => handleServiceTypeChange('home_service')}
                    className={`border-2 rounded-lg p-4 cursor-pointer flex flex-col items-center justify-center gap-2 transition-all ${formData.service_type === 'home_service' ? 'border-primary bg-blue-50 text-primary' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  <FaHome size={24} />
                  <span className="font-bold text-sm">Home Service</span>
                </div>

                {/* Roadside Assistance */}
                <div 
                    onClick={() => handleServiceTypeChange('roadside_assistance')}
                    className={`border-2 rounded-lg p-4 cursor-pointer flex flex-col items-center justify-center gap-2 transition-all ${formData.service_type === 'roadside_assistance' ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  <FaExclamationTriangle size={24} />
                  <span className="font-bold text-sm text-center">Roadside / Emergency</span>
                </div>

              </div>
            </div>

            {/* 3. Service Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Category</label>
                <select 
                  name="service_category_id" 
                  value={formData.service_category_id} 
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none bg-white"
                >
                  {SERVICE_CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date</label>
                <input 
                  type="date" 
                  name="preferred_date" 
                  required
                  // Allow today if it's roadside assistance
                  min={formData.service_type === 'roadside_assistance' ? undefined : new Date().toISOString().split('T')[0]}
                  value={formData.preferred_date} 
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Time</label>
                <select 
                  name="preferred_time_slot" 
                  value={formData.preferred_time_slot} 
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none bg-white"
                >
                  <option>Morning (8am - 12pm)</option>
                  <option>Afternoon (12pm - 4pm)</option>
                  <option>Evening (4pm - 6pm)</option>
                  {formData.service_type === 'roadside_assistance' && <option>IMMEDIATELY (Emergency)</option>}
                </select>
              </div>
            </div>

            {/* Address (Conditional for Home & Roadside) */}
            {(formData.service_type === 'home_service' || formData.service_type === 'roadside_assistance') && (
              <div className="mb-6 animate-fadeInUp bg-gray-50 p-4 rounded border border-gray-200">
                <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-2">
                    <FaMapMarkerAlt className="text-red-500" />
                    {formData.service_type === 'home_service' ? 'Service Address' : 'Breakdown Location'}
                </label>
                <input 
                  type="text" 
                  name="address" 
                  required
                  placeholder={formData.service_type === 'home_service' ? "Enter your home address" : "Enter street name, landmark, or area"}
                  value={formData.address} 
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                    We will calculate the travel fee based on this location.
                </p>
              </div>
            )}

            {/* Towing Toggle (Only for Roadside) */}
            {formData.service_type === 'roadside_assistance' && (
                <div className="mb-6 animate-fadeInUp bg-red-50 p-4 rounded border border-red-200">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input 
                            type="checkbox" 
                            name="requires_towing" 
                            checked={formData.requires_towing} 
                            onChange={handleChange} 
                            className="h-5 w-5 text-red-600 rounded focus:ring-red-500" 
                        />
                        <div>
                            <span className="block font-bold text-red-800 flex items-center gap-2">
                                <FaTruck /> I need Towing Services
                            </span>
                            <span className="text-xs text-red-600">Check this if the car cannot move and needs to be towed to the workshop.</span>
                        </div>
                    </label>
                </div>
            )}

            {/* Description */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-1">Issue Description</label>
              <textarea 
                name="description" 
                rows="4" 
                required
                placeholder="Please describe the issue or service needed in detail..."
                value={formData.description} 
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
              ></textarea>
            </div>

            <button 
              type="submit" 
              disabled={submitting}
              className={`w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-lg transition duration-300 shadow-md flex items-center justify-center gap-2 ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {submitting ? (
                <>Processing...</>
              ) : (
                <>
                  <FaTools /> Confirm Booking
                </>
              )}
            </button>

          </form>
        )}
      </div>
    </div>
  );
}

export default ServiceBookingPage;